import { omit } from 'lodash'
import { isClient } from './envChecks'
import { gtmGA3, gtmGA4 } from './googleTagManager/googleTagManager.types'

declare global {
  interface Window {
    google_tag_manager: {
      [CTM_ID: string]: {
        dataLayer: {
          reset: () => void
        }
      }
    }
  }
}

type Channel = 'flights' | 'hotels' | 'car_hire' | 'experiences'
export type SearchState = 'initial' | 'refined'

export type MemberID = `ID:${string}`

type BaseDataLayerObject = {
  domain_environment?: string
  event?: string
  channel?: Channel
  balance?: number | string
  pageLocation?: string
  booking_currency?: string
  currency?: string
  memberID?: MemberID
}

type SearchDataLayerObject = {
  booking_start_location?: null | string
  booking_end_location?: null | string
  from_date?: null | string
  to_date?: null | string
  adults?: null | number
  children?: null | number
  traveller_count?: null | number
  total_result_pages?: null | number
  shown_results?: null | number
  result_count?: null | number
  search_state?: null | SearchState
  hotel?: null | HotelDataLayerObject
  filters?: null | Record<string, string | string[]>
  mapDisplayMode?: null | string
} & BaseDataLayerObject

type FlightSearchDataLayerObject = {
  referrer?: string
  departure_date?: string
  arrival_date?: string
  flight_route?: string | null
  filter_flight_type_outbound?: string | null
  filter_flight_type_inbound?: string | null
  from_month?: string | null
} & SearchDataLayerObject

type FlightResultsDataLayerObject = {
  origin: string
  destination: string
  departure_date: string
  arrival_date: string
  flightsNumber: number
  outbound_date: string
  return_date: string
  outbound_segments: FlightResultsSegmentDataLayerObject[]
  inbound_segments: FlightResultsSegmentDataLayerObject[]
  adults: number
  children: number
  childrenAges: Array<{ child: string; age: number }>
} & BaseDataLayerObject

type FlightResultsSegmentDataLayerObject = {
  origin: string
  destination: string
  departure_date: string
  arrival_date: string
}

type FlightResultsDataLayerObject2 = {
  booking_start_location: string
  from_date: string
  to_date?: string
  traveller_count: number
  search_state: string
  result_count: number
}

type FlightDetailsDataLayerObject = {
  flight_from: string
  flight_to: string
  flight_code: string
  flight_time: string
  flight_route: string
}

type FlightDetailsDataLayerObject2 = {
  collected_avios: number
  avios_percentage: number
  items: FlightDetailsItemDataLayerObject[]
}

type FlightSelectionDataLayerObject = {
  event: string
  currency: string
  items: FlightSelectionDataLayerObject2
}

type FlightSelectionDataLayerObject2 = {
  item_id: string
  item_name: string
  item_brand: string
  item_category: string
  avios_value: number
  cash_value: number
  avios_percentage: number
  quantity: number
}

type FlightContinueToBasketDataLayerObject = {
  event: string
  currency?: string
  value: number
  avios_value: number
  cash_value: number
  avios_percentage: number
  items: FlightContinueToBasketDataLayerItemObject[]
}

type FlightContinueToBasketDataLayerItemObject = {
  item_id: string
  item_name: string
  item_brand?: string
  item_category: string
  avios_value: number
  cash_value: number
  collected_avios?: number
  price: number
  avios_percentage: number
  quantity: number
}

type FlightDetailsItemDataLayerObject = {
  index: number
  item_name: string
  item_id: string
  item_brand: string
  item_category: string
  item_list_name: string
  item_list_id: string
  price: number
  quantity: number
}

type HotelSearchDataLayerObject = {
  room_capacity: null | number
  amenities: null | string
  refundable: null | string
  bed_size: null | string
} & SearchDataLayerObject

type HotelShareDataLayerObject = {
  channel: string
  nav_element: string
  nav_type: string
  nav_action?: string
  pageLocation: string
  hotel_name: string
  traveller_count: number
  checkInDate: string
  checkOutDate: string
  guest_rating: string
  star_rating: string
}

type HotelDataLayerObject = {
  name?: string
  propertyRating?: number
  guestRating?: number
  amenities?: Array<{ title: string; amenities?: Array<string> }>
}

type CarHireDetailsDataLayerObject = {
  carHire?: {
    name?: string
    vendorName?: string
    seats?: string
    aircon?: string
    doors?: string
    vehicleClassName?: string
    vehicleTransmission?: string
  }
}

type CarHireSearchDataLayerObject = {
  different_drop_off?: null | string
  driver_under_30?: null | string
  driver_dob?: null | string
  seats?: null | number
  transmission?: null | string
  mileage_allowance?: null | string
  door_count?: null | number
  luggage_capacity?: null | string
  insurance_cover?: null | boolean
  additional_driver?: null | boolean
  child_seat?: null | boolean
  air_con?: null | string
} & SearchDataLayerObject

type ExperienceDetailsLoadObject = {
  ecommerce: {
    currency: string
    avios_value: number
    cash_value: number
    collected_avios: number
    value: number
    items: [
      {
        item_name?: string
        item_id?: string
        item_brand?: string
        item_category: string
        item_variant: string | null
        item_list_name: string
        item_list_id: string
        duration: string
        traveler_count: number
        price: number
        cash_value: number
        avios_value: number
        collected_avios: number
        quantity: number
        index: number
      }
    ]
  }
}

type ExperienceSearchDataLayerObject = {
  duration?: string
  destination: string
  programmeCode: string
  page_location: string
  referrer: string
} & SearchDataLayerObject

type ExperienceDetailDataLayerObject = {
  nav_type: string
  nav_action: string
  avios_value: number
  cash_value: number
  ticket_option: string
  traveller_count: number
  from_date: string
  to_date: string
  destination: string
  programmeCode: string
  memberID?: MemberID
  booking_currency?: string
  currency?: string
  balance?: number | string
  channel: string
  page_location: string
  referrer: string
  domain_environment: string
}

type ExperienceReplaceModalObject = {
  nav_type: string
  nav_action: string
  avios_value: string
  cash_value: string
  ticket_option: string
  traveller_count: number
  specials?: string | string[]
  price_range?: string | string[]
  duration?: string | string[]
  accessibility?: string | string[]
  from_date: string
  to_date: string
  destination: string
  programmeCode: string
  memberID?: MemberID
  booking_currency?: string
  currency?: string
  balance?: number | string
  channel: string
  page_location: string
  referrer: string
  domain_environment: string
}

type ExperienceEditSearchObject = {
  nav_type: string
  nav_action: string
  specials?: string | string[]
  price_range?: string | string[]
  duration?: string | string[]
  accessibility?: string | string[]
  from_date: string
  to_date: string
  destination: string
  programmeCode: string
  memberID?: MemberID
  booking_currency?: string
  currency?: string
  balance?: number | string
  channel: string
  page_location: string
  referrer: string
  domain_environment: string
}

type ExperienceSortByFilter = {
  nav_type: string
  nav_action: string
  sort_setting: string
  sort_state: string
  specials?: string | string[]
  price_range?: string | string[]
  duration?: string | string[]
  accessibility?: string | string[]
  from_date: string
  to_date: string
  destination: string
  programmeCode: string
  memberID?: MemberID
  booking_currency?: string
  currency?: string
  balance?: number | string
  channel: string
  page_location: string
  referrer: string
  domain_environment: string
}

type ExperiencePagination = {
  nav_type: string
  nav_action: string
  from_date: string
  to_date: string
  destination: string
  total_result_pages?: number
  shown_results?: number
  result_count?: number
  search_state: string
}

type ExperienceResultsDataLayerObject = {
  experience?: ExperienceSearchDataLayerObject
  shownResults?: number
}

type PurchaseItemGA4 = {
  item_id: string
  item_name: string
  item_variant: string
  item_brand?: string
  quantity: number
  item_category: string
  price?: number
  cash_value?: number
  value?: number
  collected_avios?: number
  index?: number
}

type HotelPurchaseItemGA4 = PurchaseItemGA4 & {
  room_capacity?: number
  bed_size?: string
  refundable?: string
}

type CarHirePurchaseItemGA4 = PurchaseItemGA4 & {
  seats?: number
  transmission?: string
  mileage_allowance?: string
  door_count?: number
  air_con?: string
  luggage_capacity?: string
}

type FlightPurchaseItemGA4 = PurchaseItemGA4 & {
  flight_route?: string
  filter_flight_type_outbound?: string
  filter_flight_type_inbound?: string
  adults?: number
  children?: number
}

type PurchaseConfirmationGA4 = {
  channel?: string
  ecommerce: {
    transaction_id: string
    currency: string
    value?: number
    tax?: number
    payment_type?: string
    items: (CarHirePurchaseItemGA4 | HotelPurchaseItemGA4 | FlightPurchaseItemGA4)[]
  }
}

type HotelBasketAnalyticsItem = {
  free_wifi?: string
  front_desk_24h?: string
  express_checkout?: string
  laundry_service?: string
  spa?: string
  smoke_free?: string
  refundable?: string
  breakfast?: string
  bed_size?: string
  room_capacity?: number
  amenities?: string[]
  room_size?: number
}

type ExperiencesBasketAnalyticsItem = {
  duration: string
  traveller_count: number
  item_list_name: string
  item_list_id: string
  price: number
}

type CarHireBasketAnalyticsItem = {
  seats?: string
  transmission?: string
  mileage_allowance?: string
  door_count?: string
  air_con?: string
  luggage_capacity?: string
  insurance_cover?: string
  additional_driver?: string
  child_seat?: string
}

type FlightBasketAnalyticsItem = {
  flight_route?: string
  filter_flight_type_outbound?: string
  filter_flight_type_inbound?: string
  adults?: string
  children?: string
}

type BaseBasketAnalyticsItemGA4 = {
  item_id: string
  item_name: string
  item_variant?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_brand?: string
  price?: number
  currency?: string
  quantity?: number | string
  avios_value?: number
  cash_value?: number
  collected_avios?: number
  taxes_and_fees?: number
  value?: number
  index?: number
}

type HotelBasketAnalyticsItemGA4 = BaseBasketAnalyticsItemGA4 & HotelBasketAnalyticsItem
type CarHireBasketAnalyticsItemGA4 = BaseBasketAnalyticsItemGA4 & CarHireBasketAnalyticsItem
type FlightBasketAnalyticsItemGA4 = BaseBasketAnalyticsItemGA4 & FlightBasketAnalyticsItem
type ExperiencesBasketAnalyticsItemGA4 = BaseBasketAnalyticsItemGA4 & ExperiencesBasketAnalyticsItem

export type BasketAnalyticsItemGA4 =
  | HotelBasketAnalyticsItemGA4
  | CarHireBasketAnalyticsItemGA4
  | FlightBasketAnalyticsItemGA4
  | ExperiencesBasketAnalyticsItemGA4

type BasketContentsGA4 = {
  channel?: string
  ecommerce: {
    currency?: string
    value?: number
    avios_value?: number
    cash_value?: number
    collected_avios?: number
    items: (
      | HotelBasketAnalyticsItemGA4
      | CarHireBasketAnalyticsItemGA4
      | FlightBasketAnalyticsItemGA4
      | ExperiencesBasketAnalyticsItemGA4
    )[]
  }
}

type AddPaymentInfo = {
  channel?: string
  ecommerce: {
    payment_type: string
    items: (HotelBasketAnalyticsItemGA4 | CarHireBasketAnalyticsItemGA4)[]
    // | ExperiencesBasketAnalyticsItemGA4
  }
}

type NewPageView = {
  channel?: string
}

export type Navigation = {
  nav_type:
    | 'cta'
    | 'text link'
    | 'notification'
    | 'footer link'
    | 'section link'
    | 'banner'
    | 'selected'
    | 'click'
  nav_action: string
  nav_element?: string
  menu_level?: string
  cancellation_policy?: string
  page_location?: string
  referrer?: string
  domain_env?: string
  programmeCode?: string
  channel?: string
}

type PopularDestinationNavigation = Navigation & {
  destination?: string
  departure_date?: string
  arrival_date?: string
  origin?: string
}

type SecureFlightNavigation = Navigation & {
  gender_provided: string
  dob_provided: string
}

type ViewTerms = {
  terms_type: string
}

type SortResults = {
  sort_setting: string
  sort_state: string
}

type SerpSortDataLayerObject = {
  channel: string
  product: string
  search_state: string
  sort_setting: string
}

type Experiment = {
  experiment_id: string
  experiment_name: string
  experiment_variant: string
  experiment_variant_id?: string
  experiment_campaign?: string
}

export type SerpFilterDataLayerObject = {
  channel?: Channel
  product?: string
  search_state?: SearchState
  accessibility?: string
}

export type HotelsFilterDataLayerObject = {
  property_type?: string
  guest_rating?: string
  star_rating?: string
  meal_plans?: string
  amenities?: string
  price_range?: string
} & SerpFilterDataLayerObject

export type CarHireFilterDataLayerObject = {
  fuel_type?: string | null
  gearbox?: string
  vendor?: string
  size?: string | null
  price_range?: string
} & SerpFilterDataLayerObject

export type ExperiencesFilterDataLayerObject = {
  specials?: string
  price_range?: string
  duration?: string
} & SerpFilterDataLayerObject

type HotelVariant = {
  hotel_name: string
  variant_name: string
  variant_context: string
}

type HotelMoreDetailsModal = Omit<HotelVariant, 'variant_context'>

type OrderHistory = {
  total_bookings: number
}

type PromotionLayerObject = {
  promoCode: string
  promoName: string
  promoType: string
  promoProduct: string
}

export type ContentfulStaticCtaDataLayerObject = {
  nav_element: string // contentful identifier of section
  nav_type: string // 'click'
  nav_action: string // '[1] {contentful section heading} | [2] {cta label/card title}'
  contentful_entry_id?: string // value as generated by the CMS
  contentful_revision_id?: number
}

export type DataLayerObject =
  | SearchDataLayerObject
  | gtmGA3
  | gtmGA4
  | FlightSearchDataLayerObject
  | FlightResultsDataLayerObject
  | FlightResultsDataLayerObject2
  | FlightSelectionDataLayerObject
  | FlightSelectionDataLayerObject2
  | FlightContinueToBasketDataLayerObject
  | FlightContinueToBasketDataLayerItemObject
  | FlightDetailsDataLayerObject
  | FlightDetailsDataLayerObject2
  | HotelSearchDataLayerObject
  | CarHireDetailsDataLayerObject
  | CarHireSearchDataLayerObject
  | PurchaseConfirmationGA4
  | ExperienceSearchDataLayerObject
  | ExperienceResultsDataLayerObject
  | ExperienceEditSearchObject
  | ExperienceSortByFilter
  | ExperienceDetailDataLayerObject
  | ExperienceReplaceModalObject
  | ExperienceDetailsLoadObject
  | ExperiencePagination
  | BasketContentsGA4
  | AddPaymentInfo
  | Navigation
  | NewPageView
  | ViewTerms
  | SortResults
  | Experiment
  | HotelShareDataLayerObject
  | HotelVariant
  | HotelMoreDetailsModal
  | OrderHistory
  | SecureFlightNavigation
  | PopularDestinationNavigation
  | PromotionLayerObject
  | SerpSortDataLayerObject
  | SerpFilterDataLayerObject
  | HotelsFilterDataLayerObject
  | CarHireFilterDataLayerObject
  | ExperiencesFilterDataLayerObject
  | ContentfulStaticCtaDataLayerObject

export const NO_DATALAYER_VALUE = 'no_datalayer_value'

const createDataLayerObject = (data?: DataLayerObject) => {
  return {
    ...data,
  }
}

export const pushToDataLayer = (data?: DataLayerObject): void => {
  if (isClient()) {
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push(createDataLayerObject(data))
  }
}

export const pushEventToDataLayer = (eventName: string, data?: DataLayerObject): void => {
  pushToDataLayer({ event: eventName, ...createDataLayerObject(data) })
}

function getSecondToLastNewPageViewIndex(dataLayer: any[]): number {
  const events = dataLayer.map((layer) => layer.event)
  const lastIdx = events.lastIndexOf('new_page_view')
  if (lastIdx === -1) {
    return -1
  }
  const beforeLast = events.slice(0, lastIdx)
  return beforeLast.lastIndexOf('new_page_view')
}

export const compareAndPushEventInDataLayer = (eventName: string, data?: DataLayerObject): void => {
  if (!isClient) {
    return
  }
  window.dataLayer = window.dataLayer ?? []

  const secondToLastIdx = getSecondToLastNewPageViewIndex(window.dataLayer)
  const recentEvents =
    secondToLastIdx === -1 ? window.dataLayer : window.dataLayer.slice(secondToLastIdx + 1)

  const isDuplicate = recentEvents.some((layer) => {
    if (layer.event !== eventName) {
      return false
    }
    const compareLayer = omit(layer, ['event', 'gtm.uniqueEventId'])
    return JSON.stringify(data) === JSON.stringify(compareLayer)
  })
  if (!isDuplicate) {
    pushEventToDataLayer(eventName, data)
  }
}

export const resetAndPushNewPageView = (data?: DataLayerObject): void => {
  pushEventToDataLayer('new_page_view', data)
}

export const toMemberId = (memberId: string | undefined): MemberID | undefined => {
  return memberId ? `ID:${memberId}` : undefined
}
